if (!customElements.get('bundle-items')) {
  const BUNDLE_ITEMS_API_VERSION = '2026-04';
  const BUNDLE_ITEMS_SECTION = 'bundle-items-item';
  const BUNDLE_ITEMS_QUERY = `query BundleComponents($id: ID!) {
    node(id: $id) {
      ... on ProductVariant {
        components(first: 20) {
          nodes {
            quantity
            productVariant { id }
          }
        }
      }
    }
  }`;

  customElements.define('bundle-items', class BundleItems extends HTMLElement {
    connectedCallback() {
      if (this.dataset.source !== 'native' || this.loadStarted) return;
      this.loadStarted = true;
      this.loadNativeBundle();
    }

    // Liquid cannot read native bundle components, so the variant's component list
    // comes from the Storefront API (tokenless) and each card is rendered by the
    // bundle-items-item section, keeping the markup identical to the override path.
    async loadNativeBundle() {
      let cards = [];
      try {
        const components = await this.fetchComponents();
        cards = (await Promise.all(components.map((component) => this.fetchCard(component)))).filter(Boolean);
      } catch (error) {
        if (window.Shopify && window.Shopify.designMode) {
          console.warn('Bundle items: the native bundle lookup failed.', error);
        }
      }

      if (cards.length) {
        this.showCards(cards);
      } else {
        this.showEmpty();
      }
    }

    async fetchComponents() {
      const response = await fetch(`${window.shopUrl || ''}/api/${BUNDLE_ITEMS_API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          query: BUNDLE_ITEMS_QUERY,
          variables: { id: `gid://shopify/ProductVariant/${this.dataset.variantId}` },
        }),
      });
      if (!response.ok) throw new Error(`Storefront API responded ${response.status}`);

      const json = await response.json();
      if (json.errors) throw new Error(JSON.stringify(json.errors));

      const node = json.data && json.data.node;
      const nodes = node && node.components ? node.components.nodes : [];
      return nodes
        .filter((component) => component && component.productVariant && component.productVariant.id)
        .map((component) => ({
          variantId: String(component.productVariant.id).split('/').pop(),
          quantity: Math.max(1, Math.floor(Number(component.quantity)) || 1),
        }));
    }

    async fetchCard({ variantId, quantity }) {
      try {
        const url = `${this.rootUrl}variants/${encodeURIComponent(variantId)}/?section_id=${BUNDLE_ITEMS_SECTION}`;
        const response = await fetch(url);
        if (!response.ok) return null;

        const html = await response.text();
        const card = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bundle-item]');
        if (!card) return null;

        this.applyQuantity(card, quantity);
        return card;
      } catch (error) {
        return null;
      }
    }

    get rootUrl() {
      const root = this.dataset.rootUrl || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    applyQuantity(card, quantity) {
      card.dataset.quantity = String(quantity);
      if (quantity < 2 || !this.dataset.quantityLabel) return;

      const label = card.querySelector('[data-bundle-item-quantity]');
      if (!label) return;
      label.textContent = this.dataset.quantityLabel.replace('[quantity]', quantity);
      label.classList.remove('hidden');
    }

    showCards(cards) {
      const list = this.querySelector('[data-bundle-items-list]');
      if (!list) return;

      // Sizes are set before the cards join the page, so no image starts loading
      // at the grid size when the block uses the list layout.
      const sizes = this.dataset.imageSizes;
      cards.forEach((card) => {
        if (sizes) card.querySelectorAll('img[sizes]').forEach((image) => image.setAttribute('sizes', sizes));
        list.append(card);
      });

      const scrollFrom = Number(this.dataset.scrollFrom);
      if (scrollFrom && cards.length >= scrollFrom && this.dataset.scrollClasses) {
        list.classList.add(...this.dataset.scrollClasses.split(/\s+/).filter(Boolean));

        // A scrolling row with no links is unreachable by keyboard, so it becomes
        // the tab stop itself, named by the heading.
        if (this.hasAttribute('data-tab-stop')) {
          const heading = this.querySelector('h2[id]');
          list.setAttribute('tabindex', '0');
          if (heading) list.setAttribute('aria-labelledby', heading.id);
        }
      }

      this.classList.remove('hidden');
    }

    // The note exists only in the theme editor, so on the storefront a product
    // that is not a bundle keeps the block hidden.
    showEmpty() {
      const note = this.querySelector('[data-bundle-items-empty]');
      if (!note) return;

      this.querySelectorAll('h2, [data-bundle-items-list]').forEach((element) => element.classList.add('hidden'));
      note.classList.remove('hidden');
      this.classList.remove('hidden');
    }
  });
}
